import React, { useEffect, useRef } from 'react';

/**
 * Live canvas backdrop: fine copper filaments drifting behind hero headlines.
 *
 * Deliberately rendered far below display resolution (a few hundred pixels
 * wide) and stretched by the compositor. The artwork is soft glow, so the
 * upscale is invisible — but it cuts the per-frame pixel work by roughly an
 * order of magnitude, which is what makes it cheap enough for phones.
 *
 * Other cost controls:
 *   - capped at 30fps; the drift is far too slow for anyone to notice
 *   - gradients built once per resize, brightness pulsing rides on globalAlpha
 *   - the ambient bloom is a static CSS gradient, not a per-frame fill
 *   - the loop stops whenever the hero is offscreen or the tab is hidden
 *   - reduced-motion renders a single frame and never starts a loop
 */

// Matches the site's copper ramp (--copper / --copper-light), same saturation.
const COLS = [
  [173, 96, 37],    // bronze
  [222, 141, 79],   // copper
  [240, 175, 122],  // copper-light
  [209, 117, 46],
];

const TAU = Math.PI * 2;

/* The buffer is sized by a total pixel budget rather than by width, so a tall
   narrow phone hero costs the same as a wide desktop one. Sizing by width
   alone made portrait buffers *larger* than landscape ones. */
const BUDGET_DESKTOP = 150000;
const BUDGET_SMALL = 70000;
const MAX_SCALE = 0.42;   // never render above this fraction of display size

// Three stroke widths fake a glow without a filter. Phones drop the widest
// one — the ambient bloom underneath is a CSS gradient and covers for it.
const PASS_FULL = [
  { wMul: 7, aMul: 0.07 },
  { wMul: 2.8, aMul: 0.2 },
  { wMul: 1, aMul: 1 },
];
const PASS_LITE = [
  { wMul: 3.2, aMul: 0.22 },
  { wMul: 1, aMul: 1 },
];

// Deterministic layout, so the look is identical on every load.
function makeLines(count) {
  let seed = 20260801;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };

  const lines = [];
  for (let i = 0; i < count; i++) {
    const depth = rnd();
    lines.push({
      y: 0.10 + 0.82 * (i / (count - 1)) + (rnd() - 0.5) * 0.05,
      amp: 0.020 + rnd() * 0.060,
      k: [0.7 + rnd() * 1.3, 1.5 + rnd() * 2.0, 2.8 + rnd() * 2.6],
      // radians/second — slow enough to stay calm, quick enough to feel alive
      sp: [
        (rnd() < 0.5 ? -1 : 1) * (0.08 + rnd() * 0.145),
        (rnd() < 0.5 ? -1 : 1) * (0.145 + rnd() * 0.23),
        (rnd() < 0.5 ? -1 : 1) * (0.23 + rnd() * 0.32),
      ],
      ph: rnd() * TAU,
      col: COLS[Math.floor(rnd() * COLS.length)],
      // the blur spreads each thread's energy, so alpha runs hot to compensate
      a: 0.22 + depth * 0.48,
      w: 0.55 + depth * 0.5,
      drift: (rnd() - 0.5) * 0.026,
      driftSp: 0.07 + rnd() * 0.10,
      pulseSp: 0.14 + rnd() * 0.21,
      pulsePh: rnd() * TAU,
      fadeIn: 0.06 + rnd() * 0.20,
      fadeOut: 0.74 + rnd() * 0.20,
    });
  }
  return lines;
}

export const WaveField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    // Transparent buffer: the ambient bloom is a CSS gradient underneath, so
    // ordinary alpha compositing replaces a full-screen blend mode.
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let W = 0, H = 0, steps = 48, frameMs = 1000 / 30;
    let lines = [];
    let grads = [];   // [lineIndex][passIndex]
    let passes = PASS_FULL;

    function build() {
      const rect = canvas.getBoundingClientRect();
      const cssW = Math.max(1, rect.width);
      const cssH = Math.max(1, rect.height);
      const small = cssW < 760;

      const budget = small ? BUDGET_SMALL : BUDGET_DESKTOP;
      const scale = Math.min(MAX_SCALE, Math.sqrt(budget / (cssW * cssH)));
      W = Math.max(140, Math.round(cssW * scale));
      H = Math.max(100, Math.round(cssH * scale));

      canvas.width = W;
      canvas.height = H;

      // Phones get a lighter budget across the board: fewer threads, fewer
      // stroke passes, coarser curves, and a slower cadence nobody can
      // perceive at this drift speed.
      passes = small ? PASS_LITE : PASS_FULL;
      steps = small ? 28 : 48;
      frameMs = small ? 1000 / 15 : 1000 / 30;

      lines = makeLines(small ? 6 : 14);

      grads = lines.map((L) => {
        const [r, g, b] = L.col;
        return passes.map((p) => {
          const a = L.a * p.aMul;
          const grad = ctx.createLinearGradient(0, 0, W, 0);
          grad.addColorStop(0, `rgba(${r},${g},${b},0)`);
          grad.addColorStop(L.fadeIn, `rgba(${r},${g},${b},${a})`);
          grad.addColorStop(0.5, `rgba(${Math.min(255, r + 34)},${Math.min(255, g + 27)},${Math.min(255, b + 20)},${a * 1.2})`);
          grad.addColorStop(L.fadeOut, `rgba(${r},${g},${b},${a})`);
          grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
          return grad;
        });
      });
    }

    function frame(t) {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);

      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';

      for (let i = 0; i < lines.length; i++) {
        const L = lines[i];
        const pulse = 0.72 + 0.28 * Math.sin(L.pulseSp * t + L.pulsePh);
        const baseY = (L.y + L.drift * Math.sin(L.driftSp * t + L.ph)) * H;

        // Trace once, stroke three times for a glow that costs no filter.
        ctx.beginPath();
        for (let s = 0; s <= steps; s++) {
          const xN = s / steps;
          let acc = 0;
          for (let h = 0; h < 3; h++) {
            acc += (0.6 / (h + 1)) *
              Math.sin(L.k[h] * xN * TAU + L.sp[h] * t + L.ph + h * 1.1);
          }
          const y = baseY + L.amp * acc * H;
          if (s === 0) ctx.moveTo(0, y);
          else ctx.lineTo(xN * W, y);
        }

        ctx.globalAlpha = pulse;
        for (let p = 0; p < passes.length; p++) {
          ctx.strokeStyle = grads[i][p];
          ctx.lineWidth = L.w * passes[p].wMul;
          ctx.stroke();
        }
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    build();

    if (reduced) {
      frame(0);
      const onResizeStatic = () => { build(); frame(0); };
      window.addEventListener('resize', onResizeStatic);
      return () => window.removeEventListener('resize', onResizeStatic);
    }

    let raf = null;
    let running = false;
    let start = null;
    let last = 0;

    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - last < frameMs) return;   // hold to the target cadence
      last = now;
      if (start === null) start = now;
      frame((now - start) / 1000);
    };

    const play = () => {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(tick);
    };
    const pause = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };

    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting && !document.hidden ? play() : pause()),
      { threshold: 0 }
    );
    io.observe(canvas);

    const onVisibility = () => (document.hidden ? pause() : play());
    document.addEventListener('visibilitychange', onVisibility);

    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 200);
    };
    window.addEventListener('resize', onResize);

    return () => {
      pause();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  return <canvas ref={canvasRef} className="wave-field" aria-hidden="true" />;
};

export default WaveField;
